<script setup lang="ts">
import { StripePosition, StripeProps } from "./Stripe.props"
import { pushHeaderClickCtaEvent } from "~/utils/analytics/clickCta"
import { useIntervalFn } from "@vueuse/core"

const props = withDefaults(defineProps<StripeProps>(), {
  backgroundColor: "#a1d7e0",
  textColor: "#000000",
  timeFrame: 5
})

const { t } = useI18n()
const {
  feedatyRatingLabel,
  feedatyReviewsCountLabel,
  feedatyRating,
  feedatyReviewsCount
} = useFeedatyRating()

type TopHighlightCard = {
  icon: string
  label: string
  backgroundColor?: string
  textColor?: string
}

const topHighlightCards = computed<TopHighlightCard[]>(() =>
  [
    {
      icon: "Parapharmacy.svg",
      label: t("header.topHighlights.paraPharmacy")
    },
    {
      icon: "Delivery.svg",
      label: t("header.topHighlights.fastShipping")
    },
    {
      icon: "Feedaty2.svg",
      label:
        feedatyRating && feedatyReviewsCount
          ? t("header.topHighlights.feedaty", {
              rating: feedatyRatingLabel,
              count: feedatyReviewsCountLabel
            })
          : ""
    }
  ].filter((item) => !!item.label)
)

// As per TF-3879, for now we put the top highlights statically; in the future we should load them dynamically from Contentful.
// Previous Contentful behaviour (Feedaty summary + CMS cards):
// const stripeCards = computed(() =>
//   props.stripePosition === StripePosition.top
//     ? [
//         { text: "", countDownInHours: false, showFeedatySummary: true },
//         ...props.stripeCardsCollection
//       ]
//     : props.stripeCardsCollection
// )
const stripeCards = computed(() =>
  props.stripePosition === StripePosition.top
    ? topHighlightCards.value
    : props.stripeCardsCollection
)

const timeFrameMilliseconds = ref(props.timeFrame * 1000)

const { pause, resume } = useIntervalFn(() => {
  handleInvertTransition(stripeCards.value.length - 1)
  visibleCardRef.value = visibleCardRef.value + 1
}, timeFrameMilliseconds)

const visibleCardRef = ref(0)

const invertTransizion = ref(false)

const visibleCard = computed(() => {
  return visibleCardRef.value % stripeCards.value.length
})

const showChevron = computed(() => {
  return stripeCards.value.length > 1
})

const bgColor = computed(() => {
  const color =
    stripeCards.value[visibleCard.value]?.backgroundColor ||
    props.backgroundColor
  return color
})

const txtColor = computed(() => {
  const color =
    stripeCards.value[visibleCard.value]?.textColor || props.textColor
  return color
})

onBeforeMount(() => {
  if (stripeCards.value.length === 1) {
    pause()
  }
})

onMounted(() => {
  const link = getLink()
  link?.addEventListener("click", (evt) => pushGtm(evt))
  onUnmounted(() => {
    link?.removeEventListener("click", (evt) => pushGtm(evt))
  })
})

const nextCard = () => {
  pause()
  handleInvertTransition(stripeCards.value.length - 1)
  visibleCardRef.value = visibleCardRef.value + 1
  resume()
}

const previousCard = () => {
  pause()
  handleInvertTransition(0)
  visibleCardRef.value =
    (visibleCardRef.value === 0 ? 3 : visibleCardRef.value) - 1
  resume()
}

const handleInvertTransition = (index: number) => {
  invertTransizion.value = visibleCard.value === index
}

const getLink = () => {
  return document.querySelector(
    `.${props.stripePosition}-header .markdown__link-underlined.markdown__link-mouse-medium > a`
  )
}

const pushGtm = (evt: Event) => {
  const text = (evt.target as HTMLAnchorElement)?.text
  pushHeaderClickCtaEvent(text)
}
</script>

<template>
  <div
    class="padded flex flex-row items-center"
    :class="[
      stripePosition === StripePosition.top ? 'py-1' : '  py-2',
      `${stripePosition}-header`,
      showChevron ? 'justify-center md:justify-between' : 'justify-center'
    ]"
    :style="{
      backgroundColor: bgColor,
      color: txtColor
    }"
  >
    <IconsChevronRight
      v-if="showChevron"
      @click="previousCard"
      class="hidden rotate-180 cursor-pointer md:block"
    />
    <div
      class="grid w-full overflow-hidden"
      :class="{ 'flex-row-reverse': invertTransizion }"
    >
      <template v-if="stripePosition === StripePosition.top">
        <HeaderUtilsTopHighlight
          v-for="(card, index) in topHighlightCards"
          :key="card.icon"
          class="col-start-1 row-start-1 w-full transition-opacity duration-500"
          :class="
            index === visibleCard
              ? 'visible opacity-100'
              : 'pointer-events-none invisible opacity-0'
          "
          :icon="card.icon"
          :label="card.label"
        />
      </template>
      <template v-else>
        <CmsComponentStripeTextCard
          v-for="(card, index) in stripeCards"
          :key="index"
          class="col-start-1 row-start-1 w-full transition-opacity duration-500"
          :class="
            index === visibleCard
              ? 'visible opacity-100'
              : 'pointer-events-none invisible opacity-0'
          "
          v-bind="card"
        />
      </template>
    </div>

    <IconsChevronRight
      v-if="showChevron"
      @click="nextCard"
      class="hidden cursor-pointer md:block"
    />
  </div>
</template>
